/**
 * Sync History Table Column Definitions
 *
 * Column configurations for the sync history BaseDataTable
 */

import React from 'react'
import { Box, Chip, Typography, Tooltip, IconButton } from '@mui/material'
import { Info as InfoIcon } from '@mui/icons-material'
import { TableColumn } from '../../../../components/shared/BaseDataTable'
import { SyncOperation } from '../../../../services/types/sync'
import { getSystemIcon } from '../../../../components/SystemIcons'
import { formatDateTime } from '../../../../utils/inspectorFormatters'

const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
  switch (status) {
    case 'completed':
      return 'success'
    case 'failed':
      return 'error'
    case 'cancelled':
      return 'warning'
    case 'running':
      return 'info'
    case 'pending':
      return 'default'
    default:
      return 'default'
  }
}

const formatDuration = (seconds?: number): string => {
  if (!seconds && seconds !== 0) return '-'
  if (seconds < 60) return `${Math.round(seconds)}s`
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = Math.round(seconds % 60)
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return `${hours}h ${remainingMinutes}m`
}

const formatRecordsProcessed = (op: SyncOperation): React.ReactNode => {
  const total = op.records_processed || 0

  if (total === 0) return <Typography variant="body2" color="text.secondary">-</Typography>

  const breakdown: string[] = []
  if (op.records_created > 0) breakdown.push(`${op.records_created} created`)
  if (op.records_updated > 0) breakdown.push(`${op.records_updated} updated`)
  if (op.records_deleted > 0) breakdown.push(`${op.records_deleted} deleted`)

  const tooltipContent = breakdown.length > 0 ? breakdown.join(' | ') : `${total} processed`

  return (
    <Tooltip title={tooltipContent}>
      <Typography variant="body2" sx={{ cursor: 'help' }}>
        {total.toLocaleString()} processed
      </Typography>
    </Tooltip>
  )
}

export const createSyncHistoryColumns = (
  onViewDetails: (operation: SyncOperation) => void
): TableColumn<SyncOperation>[] => [
  {
    key: 'status',
    label: 'Status',
    sortable: true,
    width: 110,
    render: (op) => (
      <Chip
        label={op.status}
        color={getStatusColor(op.status)}
        size="small"
        sx={{ textTransform: 'capitalize' }}
      />
    )
  },
  {
    key: 'franchise_name',
    label: 'Franchise',
    sortable: true,
    render: (op) => (
      <Box>
        <Typography variant="body2" fontWeight={500}>
          {op.fp_code?.toUpperCase()}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {op.franchise_name}
        </Typography>
      </Box>
    )
  },
  {
    key: 'sync_system',
    label: 'System',
    sortable: true,
    width: 130,
    render: (op) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {getSystemIcon(op.sync_system, { size: 20 })}
        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
          {op.sync_system}
        </Typography>
      </Box>
    )
  },
  {
    key: 'started_at',
    label: 'Started',
    sortable: true,
    hideOnMobile: true,
    render: (op) => (
      <Typography variant="body2">
        {op.started_at ? formatDateTime(op.started_at) : '-'}
      </Typography>
    )
  },
  {
    key: 'duration_seconds',
    label: 'Duration',
    sortable: true,
    width: 100,
    hideOnMobile: true,
    render: (op) => (
      <Typography variant="body2">
        {formatDuration(op.duration_seconds)}
      </Typography>
    )
  },
  {
    key: 'records_processed',
    label: 'Records',
    width: 140,
    hideOnMobile: true,
    render: (op) => formatRecordsProcessed(op)
  },
  {
    key: 'initiated_by',
    label: 'Initiated By',
    sortable: true,
    hideOnMobile: true,
    render: (op) => (
      <Typography
        variant="body2"
        sx={{
          fontStyle: op.initiated_by ? 'normal' : 'italic',
          color: op.initiated_by ? 'inherit' : 'text.secondary'
        }}
      >
        {op.initiated_by || 'System'}
      </Typography>
    )
  },
  {
    key: 'actions',
    label: 'Actions',
    width: 80,
    align: 'center',
    render: (op) => (
      <Tooltip title="View Details">
        <IconButton size="small" onClick={() => onViewDetails(op)}>
          <InfoIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    )
  }
]

export default createSyncHistoryColumns
